import { useState } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import type { AdditionalQuestion } from '@/hooks/useTenantSettings';

const MAX_ADDITIONAL_QUESTIONS = 20;

export interface AdditionalQuestionsSettingsProps {
  questions: AdditionalQuestion[];
  onChange: (questions: AdditionalQuestion[]) => void;
}

export function AdditionalQuestionsSettings({
  questions,
  onChange,
}: AdditionalQuestionsSettingsProps) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');

  const [draft, setDraft] = useState('');
  const [draftError, setDraftError] = useState(false);

  const atMax = questions.length >= MAX_ADDITIONAL_QUESTIONS;

  const handleAdd = () => {
    const label = draft.trim();
    if (!label) {
      setDraftError(true);
      return;
    }
    if (label.length > 200) return;
    if (questions.length >= MAX_ADDITIONAL_QUESTIONS) return;
    onChange([
      ...questions,
      { id: crypto.randomUUID(), label, isActive: true },
    ]);
    setDraft('');
    setDraftError(false);
  };

  const handleLabelChange = (index: number, label: string) => {
    const current = questions[index];
    if (!current) return;
    const next = [...questions];
    next[index] = { ...current, label };
    onChange(next);
  };

  const handleToggleActive = (index: number) => {
    const current = questions[index];
    if (!current) return;
    const next = [...questions];
    next[index] = { ...current, isActive: !current.isActive };
    onChange(next);
  };

  const handleDelete = (index: number) => {
    onChange(questions.filter((_, i) => i !== index));
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {t('additionalQuestions.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('additionalQuestions.description')}
      </Typography>

      <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 4 }}>
        {questions.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1, textAlign: 'center' }}>
            {t('additionalQuestions.empty')}
          </Typography>
        )}

        {questions.map((question, index) => (
          <Box
            key={question.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              py: 1,
              flexWrap: 'wrap',
            }}
          >
            <TextField
              size="small"
              sx={{ flex: 1, minWidth: 200 }}
              label={t('additionalQuestions.label')}
              value={question.label}
              onChange={(e) => handleLabelChange(index, e.target.value)}
              slotProps={{ htmlInput: { maxLength: 200 } }}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={question.isActive}
                  onChange={() => handleToggleActive(index)}
                  inputProps={{ 'aria-label': t('additionalQuestions.active') }}
                />
              }
              label={t('additionalQuestions.active')}
            />
            <Tooltip title={tc('delete')}>
              <IconButton
                size="small"
                onClick={() => handleDelete(index)}
                aria-label={tc('delete')}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ))}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
          <TextField
            size="small"
            fullWidth
            label={t('additionalQuestions.label')}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              if (draftError) setDraftError(false);
            }}
            error={draftError}
            helperText={draftError ? t('additionalQuestions.labelRequired') : undefined}
            disabled={atMax}
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
          <Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              disabled={atMax}
            >
              {t('additionalQuestions.add')}
            </Button>
          </Box>
          {atMax && (
            <Typography variant="caption" color="text.secondary">
              {t('additionalQuestions.maxReached')}
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
